import * as React from "react";
import { AlertCircle, Armchair, Loader2 } from "lucide-react";
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useStudentsList } from "@/hooks/useStudents";
import { useSeats } from "@/lib/seating/seats";
import type { Zone } from "@/lib/seating/zones";
import {
  useCreateSeatAssignment,
  type CreateSeatAssignmentInput,
  type SeatAssignment,
} from "@/lib/seating/allocations";

/**
 * Phase F-finale-7 Slice 7b.4c — Assign-seat sheet.
 *
 * Right-side drawer mirroring `<SeatEditorSheet>`: keeps the Allocations
 * `<DataTable>` visible while the admin picks a member + a seat for a
 * *permanent* (fixed-mode) allocation. The optional zone selector only
 * narrows the seat list — the assignment is (student, seat); the zone is
 * denormalised BE-side from the seat.
 *
 * The BE (`POST /api/seat-assignments`, `manage_zones`) enforces the
 * one-active-assignment-per-student and one-per-seat invariants and requires
 * the tenant's `seat_assignment_config.mode === "fixed"`, so its 409/422/400
 * land in the top-of-sheet error banner with tailored copy.
 */

const NO_ZONE = "__any__";

interface DraftState {
  student_id: string;
  zone_id: string;
  seat_id: string;
}

const EMPTY_DRAFT: DraftState = { student_id: "", zone_id: NO_ZONE, seat_id: "" };

function buildCreatePayload(draft: DraftState): CreateSeatAssignmentInput {
  return { student_id: draft.student_id, seat_id: draft.seat_id };
}

export interface AssignSeatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Zones for the (optional) seat-narrowing selector. */
  zones: Zone[];
  onSaved?: (saved: SeatAssignment) => void;
}

export function AssignSeatSheet({ open, onOpenChange, zones, onSaved }: AssignSeatSheetProps) {
  const [draft, setDraft] = React.useState<DraftState>(EMPTY_DRAFT);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(EMPTY_DRAFT);
      setSubmitError(null);
    }
  }, [open]);

  // Only active members can hold a fixed seat; the query is gated on `open`
  // so the sheet doesn't fetch until the admin actually opens it.
  const studentsQuery = useStudentsList({ status: "active", limit: 200 });
  const students = React.useMemo(() => studentsQuery.data?.items ?? [], [studentsQuery.data]);

  const zoneFilter = draft.zone_id === NO_ZONE ? undefined : draft.zone_id;
  const seatsQuery = useSeats({ zone_id: zoneFilter, is_active: true, limit: 200 });
  const seats = React.useMemo(() => seatsQuery.data?.items ?? [], [seatsQuery.data]);
  const zoneNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const z of zones) map.set(z.id, z.name);
    return map;
  }, [zones]);

  const createMutation = useCreateSeatAssignment();
  const isSubmitting = createMutation.isPending;

  const handleField = <K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const canSubmit = draft.student_id.length > 0 && draft.seat_id.length > 0 && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!canSubmit) return;

    try {
      const saved = await createMutation.mutateAsync(buildCreatePayload(draft));
      onSaved?.(saved);
      const seatNumber = seats.find((s) => s.id === saved.seat_id)?.seat_number ?? "seat";
      toast.success(`Assigned ${seatNumber}.`);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? friendlyApiMessage(err)
          : err instanceof Error
            ? err.message
            : "Couldn't assign the seat. Please retry.";
      setSubmitError(message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        data-testid="assign-seat-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <Armchair className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">Assign a seat</SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                Allocate a permanent seat to a learner. Each learner can hold one seat and each seat
                one learner — release the current allocation to reassign.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-testid="assign-seat-form"
        >
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <Field label="Learner" htmlFor="assign-student" required helper="Active learners only.">
              <Select value={draft.student_id} onValueChange={(v) => handleField("student_id", v)}>
                <SelectTrigger id="assign-student" aria-label="Learner">
                  <SelectValue placeholder="Select a learner…" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {s.admission_number ? ` · ${s.admission_number}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Zone"
              htmlFor="assign-zone"
              helper="Optional — narrows the seat list to one zone."
            >
              <Select
                value={draft.zone_id}
                onValueChange={(v) => setDraft((prev) => ({ ...prev, zone_id: v, seat_id: "" }))}
              >
                <SelectTrigger id="assign-zone" aria-label="Zone">
                  <SelectValue placeholder="Any zone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_ZONE}>Any zone</SelectItem>
                  {zones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Seat" htmlFor="assign-seat" required helper="Active seats only.">
              <Select value={draft.seat_id} onValueChange={(v) => handleField("seat_id", v)}>
                <SelectTrigger id="assign-seat" aria-label="Seat">
                  <SelectValue placeholder="Select a seat…" />
                </SelectTrigger>
                <SelectContent>
                  {seats.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.seat_number}
                      {zoneNameById.get(s.zone_id) ? ` · ${zoneNameById.get(s.zone_id)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              Assign seat
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, helper, children }: FieldProps) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {helper ? <p className="text-muted-foreground mt-1 text-xs">{helper}</p> : null}
    </div>
  );
}

function friendlyApiMessage(err: ApiError): string {
  // The seating BE nests the specific reason in `details.code` under a
  // generic top-level `CONFLICT`/`VALIDATION` code (same envelope shape as
  // the Seats surface, verified live). Its top-level messages are already
  // member-facing for the assignment invariants, so we key the nested code
  // for the two clash cases and otherwise surface the message verbatim.
  const detailCode = typeof err.details?.code === "string" ? err.details.code : undefined;
  if (detailCode === "seat.student_already_assigned") {
    return "This learner already has a seat. Release their current allocation first.";
  }
  if (detailCode === "seat.seat_already_assigned") {
    return "That seat is already taken. Release it or pick another.";
  }
  return err.message || "Couldn't assign the seat. Please retry.";
}
